<template>
  <div id="app" class="app-content mb-4">
    <app-header></app-header>
    <div class="container">
      <div class="row">
        <div class="col-lg-12 text-center">
          <div class="amej-title"><h1>Change Password</h1></div>
        </div>
      </div>

      <div class="row justify-content-center">
        <div class="col-lg-7 col-md-9">
          <div class="cp-card">
            <p class="cp-lead">
              Choose a new password for your account. You will stay signed in on this device.
            </p>

            <b-alert :show="!!successText" variant="success">{{ successText }}</b-alert>
            <div class="cp-error" v-if="showFormError">{{ formError }}</div>

            <b-form @submit.stop.prevent="submit">
              <div class="form-row-x">
                <label>Current Password *</label>
                <div class="pw-field">
                  <b-form-input
                    v-model="form.old_password"
                    :type="show.old ? 'text' : 'password'"
                    autocomplete="current-password"
                    placeholder="Your current password"
                    :class="fieldError('old_password') ? 'is-invalid' : ''"
                  ></b-form-input>
                  <a href="#" class="pw-eye" @click.prevent="show.old = !show.old">
                    <span class="material-icons">{{ show.old ? 'visibility_off' : 'visibility' }}</span>
                  </a>
                </div>
                <span class="text-danger" v-if="fieldError('old_password')">{{ fieldError('old_password') }}</span>
              </div>

              <div class="form-row-x">
                <label>New Password *</label>
                <div class="pw-field">
                  <b-form-input
                    v-model="form.new_password"
                    :type="show.next ? 'text' : 'password'"
                    autocomplete="new-password"
                    placeholder="New password"
                    :class="fieldError('new_password') ? 'is-invalid' : ''"
                  ></b-form-input>
                  <a href="#" class="pw-eye" @click.prevent="show.next = !show.next">
                    <span class="material-icons">{{ show.next ? 'visibility_off' : 'visibility' }}</span>
                  </a>
                </div>
                <span class="text-danger" v-if="fieldError('new_password')">{{ fieldError('new_password') }}</span>
              </div>

              <div class="pw-rules">
                <div class="pw-rule" v-for="rule in passwordRules" :key="rule.key" :class="rule.ok ? 'ok' : ''">
                  <span class="material-icons">{{ rule.ok ? 'check_circle' : 'radio_button_unchecked' }}</span>
                  <span>{{ rule.text }}</span>
                </div>
              </div>

              <div class="form-row-x">
                <label>Confirm New Password *</label>
                <div class="pw-field">
                  <b-form-input
                    v-model="form.confirm_password"
                    :type="show.confirm ? 'text' : 'password'"
                    autocomplete="new-password"
                    placeholder="Repeat the new password"
                    :class="fieldError('confirm_password') ? 'is-invalid' : ''"
                  ></b-form-input>
                  <a href="#" class="pw-eye" @click.prevent="show.confirm = !show.confirm">
                    <span class="material-icons">{{ show.confirm ? 'visibility_off' : 'visibility' }}</span>
                  </a>
                </div>
                <span class="text-danger" v-if="fieldError('confirm_password')">
                  {{ fieldError('confirm_password') }}
                </span>
              </div>

              <div class="cp-actions">
                <b-button variant="primary" type="submit" :disabled="isSending">
                  <b-spinner small v-if="isSending" class="mr-2"></b-spinner>
                  {{ isSending ? "Changing..." : "Change Password" }}
                </b-button>
                <router-link class="cp-cancel" :to="{ name: 'Security' }">Cancel</router-link>
              </div>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Change Password.
 *
 * The old screen asked only for the current password, then posted
 * { old_password, phone, device_id } and pushed the customer to an OTP screen for a code
 * "sent to your registered mobile number". POST /v1/users/changepassword takes
 * { old_password, new_password, confirm_password } and changes the password outright -
 * there is no OTP step and no phone in the contract, so the form could never succeed.
 */
import AppHeader from "../reuseable_components/Header.vue";
import { mapActions, mapState } from "vuex";

export default {
  name: "ChangePassword",
  components: { "app-header": AppHeader },
  data() {
    return {
      form: { old_password: "", new_password: "", confirm_password: "" },
      show: { old: false, next: false, confirm: false },
      errors: {},
      formError: "",
      successText: "",
      isSending: false,
    };
  },
  computed: {
    // Only registerErrors is read now. successmessage / errormessage are global and
    // any concurrent action can overwrite them, so the result of the call is used
    // instead of those flags.
    ...mapState(["registerErrors"]),

    passwordRules: function () {
      const p = this.form.new_password || "";
      return [
        { key: "len", text: "At least 8 characters", ok: p.length >= 8 },
        { key: "upper", text: "One uppercase letter", ok: /[A-Z]/.test(p) },
        { key: "lower", text: "One lowercase letter", ok: /[a-z]/.test(p) },
        { key: "digit", text: "One number", ok: /\d/.test(p) },
        { key: "special", text: "One special character", ok: /[^a-zA-Z0-9]/.test(p) },
        { key: "space", text: "No spaces", ok: p.length > 0 && !/\s/.test(p) },
      ];
    },
    passwordValid: function () {
      return this.passwordRules.every(function (r) { return r.ok; });
    },
    /** Do not repeat a sentence in the banner that is already under a field. */
    showFormError: function () {
      if (!this.formError) { return false; }
      const merged = Object.assign({}, this.registerErrors || {}, this.errors || {});
      const texts = Object.keys(merged).map(function (k) { return String(merged[k] || ""); });
      return texts.indexOf(String(this.formError)) === -1;
    },
  },
  mounted() {
    if (!this.$store.state.token) { this.$router.replace("/"); }
  },
  methods: {
    ...mapActions(["changepassword"]),

    /** Field errors from the server are named the same as the request fields. */
    fieldError(name) {
      if (this.errors[name]) { return this.errors[name]; }
      const server = this.registerErrors || {};
      if (name === "old_password") { return server.old_password || server.current_password || ""; }
      if (name === "new_password") { return server.new_password || server.password || ""; }
      return server[name] || "";
    },
    clearMessages() {
      this.errors = {};
      this.formError = "";
      this.successText = "";
      this.$store.commit("setRegisterErrors", {});
      this.$store.commit("setMessage", "");
      this.$store.commit("setSuccessMessage", "");
      this.$store.commit("setErrorMessage", "");
    },
    validate() {
      const e = {};
      if (!this.form.old_password) {
        e.old_password = "Your current password is required.";
      }
      if (!this.form.new_password) {
        e.new_password = "A new password is required.";
      } else if (!this.passwordValid) {
        e.new_password = "Please meet all the password requirements below.";
      } else if (this.form.new_password === this.form.old_password) {
        e.new_password = "The new password must be different from the current one.";
      }
      if (!this.form.confirm_password) {
        e.confirm_password = "Please confirm the new password.";
      } else if (this.form.confirm_password !== this.form.new_password) {
        e.confirm_password = "The two passwords do not match.";
      }
      this.errors = e;
      return Object.keys(e).length === 0;
    },
    submit() {
      this.clearMessages();
      if (!this.validate()) { return; }
      this.isSending = true;
      const vm = this;
      return this.changepassword({
        old_password: this.form.old_password,
        new_password: this.form.new_password,
        confirm_password: this.form.confirm_password,
      }).then(function (res) {
        vm.isSending = false;
        if (res && res.ok) {
          vm.successText = res.message;
          vm.form = { old_password: "", new_password: "", confirm_password: "" };
          vm.errors = {};
          return;
        }
        // Field-level complaints belong under their field, not only in the banner.
        const fe = (res && res.fieldErrors) || {};
        Object.keys(fe).forEach(function (k) { vm.$set(vm.errors, k, fe[k]); });
        vm.formError = (res && res.message) || "Unable to change your password.";
      }).catch(function () {
        vm.isSending = false;
        vm.formError = "Unable to change your password. Please try again.";
      });
    },
  },
};
</script>

<style scoped>
.cp-card {
  background: #fff; border: 1px solid rgba(0,0,0,0.08); border-radius: 10px;
  padding: 26px;
}
.cp-lead { color: #6c757d; font-size: 13px; margin-bottom: 18px; }
.form-row-x { margin-bottom: 16px; }
.form-row-x label { display: block; font-size: 14px; color: #6c757d; margin-bottom: 6px; }
.text-danger { color: #dc3545; font-size: 12px; display: block; margin-top: 4px; }
.pw-field { position: relative; }
.pw-field >>> input { padding-right: 44px; }
.pw-eye {
  position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
  color: #8a94a6; line-height: 1;
}
.pw-eye .material-icons { font-size: 19px; }
.pw-rules {
  display: grid; grid-template-columns: 1fr 1fr; grid-gap: 6px 16px;
  background: #f7f9fc; border: 1px solid #e4e9f0; border-radius: 8px;
  padding: 12px 14px; margin-bottom: 18px;
}
.pw-rule { display: flex; align-items: center; font-size: 12px; color: #8a94a6; }
.pw-rule .material-icons { font-size: 16px; margin-right: 6px; }
.pw-rule.ok { color: #00a800; }
.cp-error {
  background: #fdeaea; border: 1px solid #f5c2c7; border-radius: 6px;
  padding: 9px 12px; font-size: 13px; color: #b02a37; margin-bottom: 14px;
}
.cp-actions { display: flex; align-items: center; }
.cp-cancel { margin-left: 16px; font-size: 14px; color: #6c757d; }
@media (max-width: 575.98px) {
  .pw-rules { grid-template-columns: 1fr; }
}
</style>
